// import { PageSloDTOFromJSON } from '@atlassiansox/phobos-client-ts';
import { QueryClient } from '@tanstack/react-query';

import meta, { reports } from '@/meta';
import { EXTRA_LARGE_DESKTOP_BREAKPOINT, LARGE_DESKTOP_BREAKPOINT } from '@/styles';

import { apiFetch } from '..';

interface QuerySpec {
  queryKey: string[];
  path: string;
}

type QueryMap = {
  [key: string]: (vars: { [key: string]: string }) => QuerySpec;
};

// A map of API endpoint identifiers to functions that will produce a React
// query cache key and simple API path suitable for fetching
const queryMap: QueryMap = {
  'capability.{id}.events': ({ id }) => ({
    queryKey: ['capability', 'events', id],
    path: `/capability/${id}/events`,
  }),
  'capability.{id}.events.summary': ({ id }) => ({
    queryKey: ['capability', 'events', id, 'summary'],
    path: `/capability/${id}/events/summary`,
  }),
  // query to load all the slos for a capability with filter params
  'capability.{id}.slos-spark.{params}': ({ id, params }) => ({
    queryKey: ['capability', 'slos-spark', id, params],
    path: `/capability/${id}/slos?graph=spark&size=2000&${params}`,
  }),
  'capability.report.{params}.{page}': ({ params, page }) => ({
    queryKey: ['capability', 'report', params, page],
    path: `${reports.capability.endpoints.report}?${params}&page=${Number(page) - 1}`,
  }),
  'capability.summary.{params}': ({ params }) => ({
    queryKey: ['capability', 'summary', params],
    path: `/capability/summary?${params}`,
  }),
  'product.report.{params}.{page}': ({ params, page }) => ({
    queryKey: ['product', 'report', params, page],
    path: `${reports.product.endpoints.report}?${params}&page=${Number(page) - 1}`,
  }),
  'team.report.{params}.{page}': ({ params, page }) => {
    // "/team/search" endpoint current does not support this sorting param
    const queryParams: string = params.replace('sort=hasQualityIssues%2Cdesc', '');
    return {
      queryKey: ['team', 'report', params, page],
      path: `${reports.team.endpoints.report}?${queryParams}&page=${Number(page) - 1}`,
    };
  },
  'slo.{id}.events.list': ({ id }) => ({
    queryKey: [id, 'events', 'list'],
    path: `/slo/${id}/events/list`,
  }),
  'slo.{id}.signalfx.chartWithErrorBudget': ({ id }) => ({
    queryKey: ['slo', id, 'signalfx', 'chart'],
    path: `/slo/${id}/signalfx/chartWithErrorBudget`,
  }),
  'slo.{id}.signalfx.chartWithBurnRates': ({ id }) => ({
    queryKey: ['slo', id, 'signalfx', 'burnRateChart'],
    path: `/slo/${id}/signalfx/burnRateChart`,
  }),
  'slo.{id}': ({ id }) => ({
    queryKey: ['slo', id],
    path: `/slo/${id}`,
  }),
  'slo.{id}.events': ({ id }) => ({
    queryKey: ['slo', id, 'events'],
    path: `/slo/${id}/events`,
  }),
  'slo.{id}.lowTrafficStatus': ({ id }) => ({
    queryKey: ['slo', id, 'lowTrafficStatus'],
    path: `/slo/${id}/lowTrafficStatus`,
  }),
  'slo-group.{id}': ({ id }) => ({
    queryKey: ['slo-group', id],
    path: `${meta.slogroup.endpoints.resource}/${id}`,
  }),
  'slo-group.{id}.slos.{params}.{page}': ({ id, param, page }) => ({
    queryKey: ['slo-group', id, 'slos', param, page],
    path: `${meta.slogroup.endpoints.slos(id)}?${param}&page=${Number(page) - 1}`,
  }),
  'slo-group.{id}.members.{params}.{page}': ({ id, param, page }) => ({
    queryKey: ['slo-group', id, 'members', param, page],
    path: `${meta.slogroup.endpoints.members(id)}?${param}&page=${Number(page) - 1}`,
  }),
  'slo-group.{id}.mimir.slos.{params}.{page}': ({ id, param, page }) => ({
    queryKey: ['slo-group', id, 'slos', 'mimir', param, page],
    path: `${meta.slogroup.endpoints.slos(id, true)}?${param}&page=${Number(page) - 1}`,
  }),
  'slo-group.sync.{id}': ({ id }) => ({
    queryKey: ['slo-group', 'sync', id],
    path: meta.slogroup.endpoints.sync(id),
  }),
  'hot.search.{id}': ({ id }) => ({
    queryKey: ['past-incidents', id],
    path: `/hot/search?capabilityPublicIds=${id}&page=0&size=1000`,
  }),
  'hot.slo.{id}': ({ id }) => ({
    queryKey: ['hot', 'slo', id],
    path: meta.incidents.endpoints.slo(id),
  }),
  'tags.allowedValues': () => ({
    queryKey: ['tags', 'allowedValues'],
    path: '/slo/getAllowedTags',
  }),
  'search.{search}': ({ search }) => ({
    queryKey: ['search', search],
    path: `/search-v3/search?${search}`,
  }),
  'org.{id}': ({ id }) => ({
    queryKey: ['org', id],
    path: `/org/${id}`,
  }),
  'org.user.{username}': ({ username }) => ({
    queryKey: ['org', 'user', username],
    path: `/org/user/${username}`,
  }),
  'debug.slo.{id}': ({ id }) => ({
    queryKey: ['debug', 'slo', id],
    path: `/debug/slo/${id}`,
  }),
  'detectormonitoring.slo.{id}': ({ id }) => ({
    queryKey: ['detectormonitoring', 'slo', id],
    path: `/detectormonitoring/slo/${id}`,
  }),
};

// A centralised function for accessing simple queryMap functions
export function querySpec(key: string, vars: { [key: string]: string }): QuerySpec {
  return queryMap[key](vars);
}

// A helper for executing a prefetch of a simple parameterized API path
export async function prefetchSpec(
  queryClient: QueryClient,
  key: string,
  vars: { [key: string]: string }
) {
  const { queryKey, path } = querySpec(key, vars);
  queryClient.prefetchQuery(queryKey, () => apiFetch(path));
}

// Determines how many SLOs fit in the viewport
const determineCapabilitySloPageSize = (): number => {
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  // request a minimum of 6
  const numRows = Math.max(6, Math.ceil(windowHeight / 300));
  if (windowWidth < LARGE_DESKTOP_BREAKPOINT) {
    return numRows;
  }
  if (windowWidth < EXTRA_LARGE_DESKTOP_BREAKPOINT) {
    return numRows * 2;
  }
  return numRows * 3;
};

// Returns the query cache key and infinite fetch function for paged SLO's, used by prefetch and
// paging loaders.
export const fetchSlosSpec = (id: string) => ({
  queryKey: ['slo', 'capability', id, 'infiniteSlos'],
  fetchSlos: async ({ pageParam = 0 }) => {
    const slosJSON = await apiFetch(
      `/capability/${id}/slos?size=${determineCapabilitySloPageSize()}&page=${pageParam}`
    );
    // New phobos ts client has issue with the generated PageSloDTOFromJSON code
    // the sort property is an object but the generated code uses that as an
    // array to apply .map function as it breaks the whole page.
    // TODO: figure out why this is happening and fix it, it could be issue on phobos side
    // const slos = PageSloDTOFromJSON(slosJSON);
    // const { content, ...rest } = slos;
    const { content, ...rest } = slosJSON;
    return {
      content: content ?? [],
      ...rest,
    };
  },
});
